/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListJsonWebKeySetsRequest,
  ListJsonWebKeySetsSecurity,
} from "../models/operations/listjsonwebkeysets.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListJsonWebKeySetsQuery,
  ListJsonWebKeySetsQueryData,
  prefetchListJsonWebKeySets,
  queryKeyListJsonWebKeySets,
} from "./listJsonWebKeySets.core.js";
export {
  buildListJsonWebKeySetsQuery,
  type ListJsonWebKeySetsQueryData,
  prefetchListJsonWebKeySets,
  queryKeyListJsonWebKeySets,
};

export type ListJsonWebKeySetsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listSets jsonWebKeySets
 *
 * @remarks
 * List the organization's JSON Web Key Sets. Requires org:read.
 */
export function useListJsonWebKeySets(
  request?: ListJsonWebKeySetsRequest | undefined,
  security?: ListJsonWebKeySetsSecurity | undefined,
  options?: QueryHookOptions<
    ListJsonWebKeySetsQueryData,
    ListJsonWebKeySetsQueryError
  >,
): UseQueryResult<ListJsonWebKeySetsQueryData, ListJsonWebKeySetsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildListJsonWebKeySetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSets jsonWebKeySets
 *
 * @remarks
 * List the organization's JSON Web Key Sets. Requires org:read.
 */
export function useListJsonWebKeySetsSuspense(
  request?: ListJsonWebKeySetsRequest | undefined,
  security?: ListJsonWebKeySetsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListJsonWebKeySetsQueryData,
    ListJsonWebKeySetsQueryError
  >,
): UseSuspenseQueryResult<
  ListJsonWebKeySetsQueryData,
  ListJsonWebKeySetsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListJsonWebKeySetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListJsonWebKeySetsData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: ListJsonWebKeySetsQueryData,
): ListJsonWebKeySetsQueryData | undefined {
  const key = queryKeyListJsonWebKeySets(...queryKeyBase);

  return client.setQueryData<ListJsonWebKeySetsQueryData>(key, data);
}

export function invalidateListJsonWebKeySets(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "jsonWebKeySets", "list", ...queryKeyBase],
  });
}

export function invalidateAllListJsonWebKeySets(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "jsonWebKeySets", "list"],
  });
}
